import React, { useState, useEffect } from "react";
import { Flex, Card, Button, Col, Row, List, Popover } from "antd";
import { Commission, CommissionSetting } from "../../../types/commissionType";
import { PlusSquareOutlined } from "@ant-design/icons";
import CommissionSVG from "../../../assets/teamMember/CommissionSVG";
import { queryKeys } from "../../../services/react-query/queryKeys";
import { useQuery } from "@tanstack/react-query";
import { getAllCommission } from "../../../network/commission";
import LightbulbSVG from "../../../assets/teamMember/LightbulbSVG";
import DeleteSVG from "../../../assets/common/DeleteSVG";
import EditSVG from "../../../assets/teamMember/EditSVG";
import DeleteCommission from "./DeleteCommission";
import { useIntl } from "react-intl";
import { getFormatedCycleString } from "./commissionsUtils";
import { useSelector } from "react-redux";
import AddAndEditCommission from "./AddAndEditCommission";

const Commissions: React.FC<{ teamMemberData: any }> = ({ teamMemberData }) => {
  const intl = useIntl();
  const { currentLang } = useSelector(
    (state: { user: { currentLang: "ar" | "en" } }) => state.user
  );
  const formateCommissionTypeLocalization = (commissionType: string) => {
    if (currentLang === "ar") {
      return commissionType;
    }
    if (commissionType === "خدمات") {
      return "Services";
    } else if (commissionType === "منتجات") {
      return "Products";
    } else if (commissionType === "العضويات") {
      return "Memberships";
    } else if (commissionType === "كروت الهدايا") {
      return "Gift Cards";
    } else {
      return commissionType;
    }
  };
  const CommissionCard: React.FC<{ item: Commission; index: number }> = ({
    item,
    index,
  }) => {
    const commissionsByType: Record<string, CommissionSetting[]> = {};
    item?.commissions.forEach((commission) => {
      const commissionType = commission.commission_type;
      commissionsByType[commissionType] =
        commissionsByType[commissionType] || [];
      commissionsByType[commissionType].push(commission);
    });
    const renderCommissionContent = (commissions: CommissionSetting[]) => (
      <div>
        <ul className="list-disc text-[#00086D]">
          {commissions.map((commission, index) => (
            <li key={index} className="m-3">
              <Flex justify="space-between">
                <span className="px-1">
                  {commission.from}{" "}
                  {intl.formatMessage({
                    id: "SARshort",
                    defaultMessage: "ر.س",
                  })}
                </span>
                -
                <span className="px-1">
                  {commission.to}{" "}
                  {intl.formatMessage({
                    id: "SARshort",
                    defaultMessage: "ر.س",
                  })}
                </span>
                <span className="bg-color-light px-2 rounded-md mx-3">
                  <span className="text-gray-400 px-1">
                    {intl.formatMessage({
                      id: "commission",
                      defaultMessage: "العمولة",
                    })}
                    :
                  </span>
                  <span>
                    {commission.commission_type === "percentage"
                      ? `${commission.value} % `
                      : `${commission.value} ${
                          commission.value_type === "fixed"
                            ? intl.formatMessage({
                                id: "SARshort",
                                defaultMessage: "ر.س",
                              })
                            : "%"
                        }`}
                  </span>
                </span>
              </Flex>
            </li>
          ))}
        </ul>
      </div>
    );

    // Group commissions by commission_type
    const groupedCommissions: { [key: string]: CommissionSetting[] } = {};
    item?.commissions.forEach((commission) => {
      const commissionType = commission.commission_type;
      if (!groupedCommissions[commissionType]) {
        groupedCommissions[commissionType] = [];
      }
      groupedCommissions[commissionType].push(commission);
    });

    const commissionButtons = Object.entries(groupedCommissions).map(
      ([commissionType, commissions], index) => (
        <Popover
          key={index}
          content={renderCommissionContent(commissions)}
          title={formateCommissionTypeLocalization(commissionType)}
          trigger="hover"
        >
          <Button
            className="bg-[#FBFBFF] mx-1"
            style={{
              height: "32px",
              borderRadius: 2,
              paddingTop: 0,
              paddingBottom: 0,
            }}
          >
            <LightbulbSVG />
            {formateCommissionTypeLocalization(commissionType)}
          </Button>
        </Popover>
      )
    );

    return (
      <List.Item>
        <Card className="w-full ">
          <div className="flex  gap-1 flex-col md:flex-row justify-center md:justify-between">
            <div className="overflow-auto flex gap-1  flex-col md:flex-row justify-center md:justify-between ">
              <div className="gutter-row text-[14px] min-w-[100px]">
                <span className="p-1">
                  <CommissionSVG />
                </span>
                <span>
                  {intl.formatMessage({
                    id: "commission",
                    defaultMessage: "العمولة",
                  })}{" "}
                  {item?.id}#
                </span>
              </div>
              <div className="gutter-row text-[14px] min-w-[190px]">
                <div className="bg-color-light rounded-md py-1 px-2 ">
                  <span className="text-gray-400">
                    {intl.formatMessage({
                      id: "appliedDate",
                      defaultMessage: "تاريخ بدأ التطبيق",
                    })}
                    :
                  </span>
                  <span>{item?.effective_date}</span>
                </div>
              </div>
              <div className="gutter-row text-[14px] min-w-[170px]">
                <div className="bg-color-light rounded-md py-1 px-2">
                  <span className="text-gray-400">
                    {intl.formatMessage({
                      id: "commissionRecurrence",
                      defaultMessage: "تكرار العمولة",
                    })}{" "}
                    :
                  </span>
                  <span>
                    {getFormatedCycleString(
                      item?.commission_cycle as any,
                      currentLang
                    )}
                  </span>
                </div>
              </div>
              <div className="gutter-row text-[14px] min-w-[150px]">
                <span className="text-gray-400 ">
                  {" "}
                  {intl.formatMessage({
                    id: "commissionAppliesTo",
                    defaultMessage: "العمولة سارية على",
                  })}
                  :
                </span>
              </div>
              <div className="text-[14px]">
                <div className="flex overflow-x-auto space-x-1">
                  {commissionButtons}
                </div>
              </div>
            </div>

            <div className=" flex gap-1">
              <Button
                type="text"
                htmlType="button"
                onClick={() => openModalEdit("EditCommission", item)}
              >
                <EditSVG />
              </Button>

              <Button
                type="text"
                onClick={() => openModalEdit("DeleteCommission", item)}
                htmlType="button"
              >
                <DeleteSVG />
              </Button>
            </div>
          </div>
        </Card>
      </List.Item>
    );
  };

  const [editCommissionData, setEditCommissionData] =
    useState<Commission | null>(null);

  const openModalEdit = (modal: any, commissionData: Commission) => {
    setActiveModal(modal);
    setEditCommissionData(commissionData);
  };

  const { data: listCommission } = useQuery([queryKeys.listCommission], () =>
    getAllCommission(teamMemberData?.id)
  );

  const [activeModal, setActiveModal] = useState(null);

  const openModal = (modal: any) => {
    setActiveModal(modal);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  const modalComponents = {
    AddCommission: (
      <AddAndEditCommission
        visible={true}
        onClose={closeModal}
        team_member_id={teamMemberData?.id}
      />
    ),
    EditCommission: (
      <AddAndEditCommission
        visible={true}
        onClose={closeModal}
        team_member_id={teamMemberData?.id}
        data={editCommissionData}
      />
    ),
    DeleteCommission: (
      <DeleteCommission
        visible={true}
        onClose={closeModal}
        team_member_id={teamMemberData.id}
        data={editCommissionData}
      />
    ), // Pass index as a prop
  };

  const renderModal = activeModal ? modalComponents[activeModal] : null;

  return (
    <div className="tabs bg-[#EEF0FF] p-4">
      {renderModal}

      <div className="text-base font-normal leading-7">
        {intl.formatMessage({
          id: "memberCommission",
          defaultMessage: "عمولة العضو",
        })}
      </div>
      <List
        grid={{
          gutter: 14,
          xs: 1,
          sm: 1,
          md: 1,
          lg: 1,
          xl: 1,
          xxl: 1,
        }}
        className="mt-4"
        dataSource={listCommission?.data}
        renderItem={(item: Commission, index: number) => (
          <CommissionCard key={item.id} item={item} index={index} />
        )}
      />
      <Flex justify="flex-end" className="my-4">
        <Button
          type="primary"
          className=""
          htmlType="submit"
          onClick={() => openModal("AddCommission")}
          icon={<PlusSquareOutlined />}
        >
          {intl.formatMessage({
            id: "addNewCommission",
            defaultMessage: "اضف عمولة جديدة",
          })}
        </Button>
      </Flex>
    </div>
  );
};

export default Commissions;
